import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, Hash, Info, Scales, ArrowClockwise } from '@phosphor-icons/react';

export default function LegalSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefix, setPrefix] = useState('LAW');
  const [pad, setPad] = useState(6);
  const [claimTypes, setClaimTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/legal/settings');
      setPrefix(data.numbering_prefix || 'LAW');
      setPad(data.numbering_pad || 6);
      setClaimTypes(data.claim_types || []);
      setStatuses(data.statuses || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!prefix.trim()) { toast.error('البادئة مطلوبة'); return; }
    const p = parseInt(pad);
    if (isNaN(p) || p < 3 || p > 10) { toast.error('عدد الخانات بين 3 و 10'); return; }
    setSaving(true);
    try {
      await api.put('/legal/settings', { numbering_prefix: prefix.trim().toUpperCase(), numbering_pad: p });
      toast.success('تم حفظ الإعدادات');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحفظ');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-400">جارٍ التحميل...</div>;

  const sample = (typeCode = 'FIN') => `${prefix.toUpperCase()}-${typeCode}-${String(1).padStart(pad, '0')}`;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="legal-settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#1E3A5F] mb-1">Legal Department Settings</div>
        <h1 className="text-3xl font-bold text-slate-900">إعدادات الإدارة القانونية</h1>
        <p className="text-sm text-slate-500 mt-1">تحكم في ترقيم المطالبات وأنواعها وحالاتها</p>
      </div>

      {/* Numbering */}
      <Card className="p-6 border border-slate-200 rounded-md shadow-none">
        <div className="flex items-center gap-2 mb-1">
          <Hash className="text-[#1E3A5F]" size={20} weight="duotone" />
          <h2 className="text-lg font-bold text-slate-900">ترقيم المطالبات</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">صيغة الترقيم التلقائي: <span className="mono">PREFIX-TYPE-SEQUENCE</span></p>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm mb-1 block">البادئة (Prefix)</Label>
            <Input value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} maxLength={6} data-testid="settings-prefix" className="uppercase" />
            <div className="text-xs text-slate-500 mt-1">حروف إنجليزية فقط (3–6 أحرف). المستخدمة حالياً: <span className="mono font-semibold">LAW</span></div>
          </div>
          <div>
            <Label className="text-sm mb-1 block">عدد خانات الرقم التسلسلي</Label>
            <Input type="number" min={3} max={10} value={pad} onChange={(e) => setPad(parseInt(e.target.value) || 6)} data-testid="settings-pad" />
            <div className="text-xs text-slate-500 mt-1">بين 3 و 10 خانات (يُملأ بأصفار)</div>
          </div>
        </div>

        {/* Live preview */}
        <div className="mt-5 p-4 bg-slate-50 border border-slate-200 rounded-md">
          <div className="text-xs text-slate-500 mb-2 flex items-center gap-1"><Info size={12} /> معاينة مباشرة لأرقام المطالبات:</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {claimTypes.map(t => (
              <div key={t.code} className="p-2.5 bg-white border border-slate-200 rounded-md text-center">
                <div className="text-[10px] text-slate-500 mb-0.5">{t.label_ar}</div>
                <div className="mono text-sm font-semibold text-slate-900">{sample(t.code)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <Button onClick={save} disabled={saving} className="bg-slate-900 text-white" data-testid="settings-save-btn">
            <ShieldCheck className="ms-1" size={16} /> {saving ? 'جارٍ الحفظ...' : 'حفظ الإعدادات'}
          </Button>
          <Button variant="outline" onClick={load} data-testid="settings-reload-btn">
            <ArrowClockwise className="ms-1" size={16} /> إعادة التحميل
          </Button>
        </div>
      </Card>

      {/* Claim types */}
      <Card className="p-6 border border-slate-200 rounded-md shadow-none">
        <div className="flex items-center gap-2 mb-4">
          <Scales className="text-[#1E3A5F]" size={20} weight="duotone" />
          <h2 className="text-lg font-bold text-slate-900">أنواع المطالبات المعتمدة</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {claimTypes.map(t => (
            <div key={t.code} className="p-3 border border-slate-200 rounded-md flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">{t.label_ar}</div>
                <div className="text-xs text-slate-500 mt-0.5">مثال: <span className="mono">{sample(t.code)}</span></div>
              </div>
              <div className="text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-700 mono">{t.code}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Statuses */}
      <Card className="p-6 border border-slate-200 rounded-md shadow-none">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="text-[#1E3A5F]" size={20} weight="duotone" />
          <h2 className="text-lg font-bold text-slate-900">حالات المطالبات</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {statuses.map((s, i) => (
            <div key={s.code} className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-full text-sm">
              <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10px] font-bold">{i + 1}</div>
              <span className="text-slate-900">{s.label_ar}</span>
              <span className="mono text-[10px] text-slate-400">{s.code}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3 leading-loose">
          هذه هي مراحل سير المطالبات القانونية. تُستخدم في تصفية القضايا وتتبع تقدمها.
        </p>
      </Card>
    </div>
  );
}
